import {HttpErrorResponse} from '@angular/common/http';
import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {SelectDropDownComponent} from 'ngx-select-dropdown';
import {Subscription, of, first} from 'rxjs';
import {switchMap} from 'rxjs/operators';

import {Constant, RoleName, ServiceTypeNames} from 'src/app/@shared/constants/Constant';
import {RepositionType} from 'src/app/@shared/enums/reposition-type.enum';
import {SplitRecordDialogComponent} from 'src/app/MaterialDialogs/split-record-dialog/split-record-dialog.component';
import {ISplitRecordPopupDataViewModel} from 'src/app/MaterialDialogs/split-record-dialog/split-record-dialog.model';
import {ILoggedInUserViewModel} from 'src/app/model/common.model';
import {CustomerList} from 'src/app/service/customer.model';
import {ExportService} from 'src/app/service/exportCSV.service';
import {filterServiceRequestData} from 'src/app/service/filterData.model';
import {LocationService} from 'src/app/service/location.service';
import {NGXSelectDropDownService} from 'src/app/service/ngx-select-dropdown-service';
import {ReportService} from 'src/app/service/report.service';
import {ServiceRequestService} from 'src/app/service/service-request.service';
import {VehicleService} from 'src/app/service/vehicle.service';
import {VendorService} from 'src/app/service/vendor.service';
import {ICommonType} from 'src/app/shared/interfaces/common-type.interface';
import {environment} from 'src/environments/environment';
import {
  IBlockTimeItem,
  IBlocktimeReportSorting,
  IBlockTimeRequestResponse,
  IBlockTimeSums
} from './block-time-report.model';
import {reportMonthYearList} from '@utils/report-time-year';
import {BaseDropdownInterface} from '@interfaces/base-dropdown.interface';
import {AircraftInterface} from './interfaces/aircraft.interface';
import {NotificationService} from '@notification/service';
import {subTotalReportSum, totalOfBlockTimeReportValues} from './block-time-report.config';
import {MemberService} from '../../service/member.service';

declare var $: any;

@Component({
  selector: 'app-block-time-report',
  templateUrl: './block-time-report.component.html',
  styleUrls: ['./block-time-report.component.css'],
})
export class BlockTimeReportComponent implements OnInit, OnDestroy {
  @Input() filterReport: filterServiceRequestData;
  @Input() searchText: string;
  @ViewChild('cancelBtnRef') cancelBtnRef: ElementRef<HTMLElement>;
  customerList: CustomerList[] = [];

  private _subscriptions: Subscription = new Subscription();

  blockTimeReportData: IBlockTimeItem[] = [];
  blockTimeReportCsvData = [];
  blockTimeReportRecord: IBlockTimeItem;
  isOpenAddEditBlockTimeRecordPopUp: boolean = false;
  loader: boolean = false;
  isEditBlockTimeRecord: boolean = false;
  timeZone;
  currentPageNumber: number = 1;
  currentPage: number = 1;
  selectedPage: number = 10;
  pagination: string;
  responsive: any;
  maxDate = new Date().toISOString().slice(0, 16);
  sortBy: string;
  sortOrder: boolean;
  blockTimeReportSorting: IBlocktimeReportSorting;
  user: ILoggedInUserViewModel;
  isInternalReadOnly: boolean = false;
  itineraryPageRedirectUrl = `${environment.redirectURL}`;
  airportCodes: string[] = [];
  airportCodeDropDownSettings = {
    displayKey: 'airportCode',
    search: true,
    placeholder: 'Select OB Repo',
    height: '450px'
  };
  tailNumberDropdownConfig = {
    displayKey: 'tailNumber',
    search: true,
    placeholder: 'Service Request Number',
    height: '450px'
  };
  totalOfBlockTimeReportValues = totalOfBlockTimeReportValues;
  totalSum: IBlockTimeSums | null = null;
  customerListDropdownSetting = {
    displayKey: 'customerName',
    search: true,
    placeholder: 'Select Customer',
    height: '450px'
  };
  monthYearDropdownSetting = {
    search: false,
    placeholder: 'Select Month/Year',
    height: '450px'
  };
  tripDropdownConfig = {
    search: true,
    placeholder: 'Service Request Number',
    height: '450px'
  };
  airVendorConfig = {
    search: true,
    placeholder: 'Select Air Vendor',
    height: '450px',
    displayKey: 'name',
  };
  serviceTypes: ICommonType[] = [];
  organTypes: ICommonType[] = [];
  groundVendorList: ICommonType[] = [];
  airVendorList: ICommonType[] = [];
  aircraftTypeList: ICommonType[] = [];
  aircraftSizeList: ICommonType[] = [];
  monthYearList = reportMonthYearList();
  trips: string[] = [];
  aircrafts: AircraftInterface[] = [];
  airVendorValue: ICommonType | null = null;
  tailNumberValue: AircraftInterface | null = null;

  constructor(
    private _reportService: ReportService,
    private _exportService: ExportService,
    private _serviceRequestService: ServiceRequestService,
    private _locationService: LocationService,
    private _dialog: MatDialog,
    private _ngxSelectDropDownService: NGXSelectDropDownService,
    private _vendorService: VendorService,
    private _vehicleService: VehicleService,
    private readonly memberService: MemberService,
    private readonly notificationService: NotificationService,
  ) {
    this.user = JSON.parse(localStorage.getItem('user'));
    this.isInternalReadOnly = this.user.role === RoleName.InternalReadOnly;

    this._reportService.loaderState.next(true);

    this._subscriptions.add(this._serviceRequestService.timZoneChange.subscribe(() => {
      this.timeZone = Constant.selectedTimeZoneShortValue;
    }));

    this._subscriptions.add(this._reportService.openAddBlockTimeRecordPopUp.subscribe(() => {
      this.openAddBlockTimeRecordPopUp();
    }));

    this._subscriptions.add(this._reportService.filterBlockTimeReportData.pipe(switchMap(() => of(true))).subscribe(() => {
      this.getBlockTimeReportData();
    }));

    this._subscriptions.add(this._reportService.showCountForBlockTimeReport.subscribe((response: number) => {
      this.selectedPage = response;
      this.currentPageNumber = 1;
      this.updatePagination();
    }));

    this.getAllAirportCodeList();
    this.initializeBlocktimeReportSorting();
    this.initializeBlockTimeRecordPopupDropDowns();
  }

  ngOnInit(): void {
    this.getTripsBasedOnTripNumber();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  initializeBlocktimeReportSorting() {
    this.sortBy = 'srCreatedOnDate';
    this.sortOrder = false;
    this.blockTimeReportSorting = {
      srCreatedOnDate: false,
      tripID: false,
      customerName: false,
      serviceType: false,
      additionalOrgans: false,
      originAddress: false,
      destinationAddress: false,
      unosid: false,
      airVendor: false,
      routing: false,
      tailNumber: false,
      aircraftType: false,
      actualBlockTime: false,
      aircraftSize: false,
      base: false,
      cost: false,
      profit: false,
      rate: false,
      invoiceID: false,
      ar: false,
      ap: false,
      estimatedBlockHours: false,
      groundVendor: false,
      inboundReposition: false,
      outboundReposition: false,
      flightMargin: false,
      ibEstimatedBlockHours: false,
      obEstimatedBlockHours: false,
      liveFlightHours: false,
      cancellationHours: false,
    };
  }

  getBlockTimeReportData() {
    this._reportService.fetchBlockTimeReport(this.filterReport).subscribe({
      next: (response: IBlockTimeRequestResponse) => {
        this.blockTimeReportData = response != null ? response.blockTimeItems.sort((firstRecord, secondRecord) => {
          const firstDate = new Date(firstRecord.srCreatedOnDate);
          const secondDate = new Date(secondRecord.srCreatedOnDate);
          return (Number(firstDate) > Number(secondDate)) ? 1 : ((Number(firstDate) < Number(secondDate)) ? -1 : 0);
        }) : [];
        this.totalSum = response.sums;
        this.currentPageNumber = 1;
        this.initializeBlocktimeReportSorting();
        this._reportService.loaderState.next(false);
        this.updatePagination();
        this.calculateTotalValues();
      },
      error: (error) => {
        console.log(error);
        this.blockTimeReportData = [];
        this.notificationService.warning('Something went wrong while getting blockTime report!');
        this._reportService.loaderState.next(false);
      },
    });
  }

  generateBlockTimeCsvReport(reportName: string) {
    this.prepareBlockTimeReportCsvData();
    this._exportService.exportAsExcelSingleFile(this.blockTimeReportCsvData, reportName);
  }

  prepareBlockTimeReportCsvData() {
    this.blockTimeReportCsvData = [];
    this.blockTimeReportData.forEach((reportData) => {
      this.blockTimeReportCsvData.push({
        Date: this._reportService.convertToMST(reportData.srCreatedOnDate),
        'Trip #': reportData.tripID,
        Customer: reportData.customer.customerName,
        'Service Request Type': reportData.serviceType,
        'Organ Type': reportData.additionalOrgans,
        'Unos ID': reportData.unosid,
        'Ground Vendor': reportData.groundVendor,
        'Air Vendor': reportData.airVendor,
        'OB Repo': reportData.outboundReposition,
        'OB Repo Hours': reportData.obEstimatedBlockHours,
        Routing: reportData.routing,
        'IB Repo': reportData.inboundReposition,
        'IB Repo Hours': reportData.ibEstimatedBlockHours,
        'Tail Number': reportData.tailNumber,
        'Aircraft Types': reportData.aircraftType,
        'Est. Block Hours': reportData.estimatedBlockHours,
        'Actual Block Time': reportData.actualBlockTime,
        'Cancellation Hours': reportData.cancellationHours,
        'Billed Hours': reportData.totalBilledHours,
        'Aircraft Size': reportData.aircraftSize,
        Cost: reportData.cost,
        Profit: reportData.profit,
        Rate: `${reportData.rate != null ? reportData.rate + ' %' : ''}`,
        'Flight Margin': `${reportData.flightMargin != null ? reportData.flightMargin + ' %' : ''}`,
        'Invoice #': reportData.invoiceID,
        'A/R': reportData.ar === true ? 'Yes' : 'No',
        'A/P': reportData.ap === true ? 'Yes' : 'No',
        Note: reportData.note
      });
    });
  }

  openAddBlockTimeRecordPopUp() {
    this.isEditBlockTimeRecord = false;
    this.initializeAddBlockTimeRecordPopUp();
    this.isOpenAddEditBlockTimeRecordPopUp = true;
    this.getCustomers();
  }

  private initializeBlockTimeRecordPopupDropDowns() {
    if (this.serviceTypes.length === 0) {
      this.getAllServiceTypes();
    }
    this.getAllAirAndGroundVehicleList();
    if (this.aircraftTypeList.length === 0) {
      this.getAllAircraftList();
    }
    if (this.aircraftSizeList.length === 0) {
      this.getAllAircraftCategory();
    }
  }

  getAllAircraftCategory() {
    this._subscriptions.add(this._vehicleService.getAircraftCategoryList().subscribe((response) => {
      this.aircraftSizeList = response.map(x => {
        return {id: x.appCategoryId, name: x.name, type: x.appCategoryLevel};
      });
    }));
  }

  private getAllAircraftList() {
    this._subscriptions.add(this._vehicleService.getAllAircraftList().subscribe((response) => {
      this.aircraftTypeList = response;
    }));
  }

  getAllAirAndGroundVehicleList(): void {
    this.getAllAirVehicleList();
    this.getAllGroundVehicleList();
  }

  getAllGroundVehicleList(): void {
    if (this.groundVendorList.length === 0) {
      this._subscriptions.add(this._vendorService.getAllGroundVendorList().subscribe((response) => {
        this.groundVendorList = response;
      }));
    }
  }

  getAllAirVehicleList(): void {
    const allAirVendorListSubs = this._vendorService.getAllAirVendorList()
      .subscribe((response: ICommonType[]): void => {
        this.airVendorList = response;
      });

    this._subscriptions.add(allAirVendorListSubs);
  }

  getAllServiceTypes(): void {
    this._subscriptions.add(this._serviceRequestService.getServiceTypes().subscribe((response) => {
      this.serviceTypes = response.map(x => {
        return {id: x.serviceTypeId, name: x.serviceTypeName, type: ''};
      });
      this.getAllOrganTypes();
    }, () => {
      this.serviceTypes = [];
    }));
  }

  getAllOrganTypes(): void {
    if (this.serviceTypes.length > 0 && this.organTypes.length === 0) {
      const organTypeId: string = this.serviceTypes.find(x => x.name === ServiceTypeNames.organ).id;
      if (organTypeId !== undefined) {
        this._subscriptions.add(this._serviceRequestService.getOrgenType(organTypeId).subscribe((response) => {
          this.organTypes = response.map(x => {
            return {id: x.jobTypeId, name: x.jobTypeName, type: ''};
          });
        }, (error) => {
          this.organTypes = [];
        }));
      }
    }
  }

  focusNgxSelectDropDownSearch(selectedNgxSelectDropDown: SelectDropDownComponent): void {
    this._ngxSelectDropDownService.applySearchFocus(selectedNgxSelectDropDown);
  }

  returnConfiguration(configId: string) {
    if (configId === RepositionType.Base) {
      this.airportCodeDropDownSettings.placeholder = 'Select Base';
    } else if (configId === RepositionType.OBRepo) {
      this.airportCodeDropDownSettings.placeholder = 'Select OB Repo';
    } else if (configId === RepositionType.IBRepo) {
      this.airportCodeDropDownSettings.placeholder = 'Select IB Repo';
    }
    return this.airportCodeDropDownSettings;
  }

  returnUpdatedBlockTimeReportValues(record: IBlockTimeItem): IBlockTimeItem {
    this.aircrafts = [];
    this.airVendorValue = this.airVendorList.find(item => item.name === record.airVendor);

    if (this.airVendorValue) {
      const aircraftForVendorsSubs = this._reportService.getAircraftsForVendors(this.airVendorValue.id).subscribe(data => {
        this.aircrafts = data;

        this.tailNumberValue = this.aircrafts.find(item => item.tailNumber === record.tailNumber);
      });

      this._subscriptions.add(aircraftForVendorsSubs);
    }

    return {
      ActualEndTime: record.ActualEndTime,
      ActualStartTime: record.ActualStartTime,
      LastUpdatedBy: record.LastUpdatedBy,
      additionalOrgans: record.additionalOrgans,
      airVendor: this.airVendorValue.name,
      aircraftSize: record.aircraftSize,
      aircraftType: record.aircraftType,
      ap: record.ap,
      ar: record.ar,
      blockTimeReportID: record.blockTimeReportID,
      cost: record.cost,
      customer: this.customerList.find(x => x.customerName === record.customer.customerName),
      flightMargin: record.flightMargin,
      groundVendor: record.groundVendor,
      outboundReposition: record.outboundReposition,
      inboundReposition: record.inboundReposition,
      invoiceID: record.invoiceID,
      isContracted: record.isContracted,
      isRecordManuallyAdded: record.isRecordManuallyAdded,
      isRecordSplitted: record.isRecordSplitted,
      lastUpdatedOn: record.lastUpdatedOn,
      note: record.note,
      plannedEndTime: record.plannedEndTime,
      plannedStartTime: record.plannedStartTime,
      primaryOrgan: record.primaryOrgan,
      profit: record.profit,
      rate: record.rate,
      region: record.region,
      routing: record.routing,
      serviceType: record.serviceType,
      srCreatedOnDate: record.srCreatedOnDate,
      tailNumber: record.tailNumber,
      tripID: record.tripID,
      unosid: record.unosid,
      monthYear: record.monthYear,
      ibEstimatedBlockHours: this.initHoursValue(Number(record.ibEstimatedBlockHours), record.isCancelledNoCost, 0),
      obEstimatedBlockHours: this.initHoursValue(Number(record.obEstimatedBlockHours), record.isCancelledNoCost, 0),
      liveFlightHours: this.initHoursValue(Number(record.liveFlightHours), record.isCancelledNoCost, 0),
      actualBlockTime: this.initHoursValue(Number(record.actualBlockTime), record.isCancelledNoCost, 0),
      cancellationHours: this.initHoursValue(Number(record.cancellationHours), record.isCancelledNoCost, 1),
      estimatedBlockHours: this.initHoursValue(Number(record.estimatedBlockHours), record.isCancelledNoCost, 1),
      isCancelledNoCost: record.isCancelledNoCost,
      totalBilledHours: this.initHoursValue(Number(record.totalBilledHours), record.isCancelledNoCost, 0),
    };
  }

  openEditBlockTimeReport(record: IBlockTimeItem): void {
    this.monthYearList = reportMonthYearList(record.srCreatedOnDate);
    this.isOpenAddEditBlockTimeRecordPopUp = true;
    this.initializeAddBlockTimeRecordPopUp();
    this.initializeBlockTimeRecordPopupDropDowns();
    this.isEditBlockTimeRecord = true;
    this.blockTimeReportRecord = this.returnUpdatedBlockTimeReportValues(record);
    this.calculateActualBlockTime();
    this.getCustomers(record);
  }

  addEditBlockTimeRecord(blockTimeReportForm: NgForm): void {
    if (blockTimeReportForm.valid) {
      this.aircrafts = [];
      this.blockTimeReportRecord.tailNumber = this.tailNumberValue.tailNumber;

      this._reportService.loaderState.next(true);

      if (!this.isEditBlockTimeRecord) {
        this.initializeAddBlockTimeRecordPopUp(this.convertFormToRecord(blockTimeReportForm));
        this.getCustomers();
      } else {
        this.editBlockTimeReportData(blockTimeReportForm);
      }

      if (!this.blockTimeReportRecord.monthYear.length) {
        this.blockTimeReportRecord.monthYear = '';
      }

      this.blockTimeReportRecord.obEstimatedBlockHours = Number(this.blockTimeReportRecord.obEstimatedBlockHours) ?? null;
      this.blockTimeReportRecord.ibEstimatedBlockHours = Number(this.blockTimeReportRecord.ibEstimatedBlockHours) ?? null;
      this.blockTimeReportRecord.cancellationHours = Number(this.blockTimeReportRecord.cancellationHours) ?? null;
      this.blockTimeReportRecord.liveFlightHours = Number(this.blockTimeReportRecord.liveFlightHours) ?? null;

      this._subscriptions.add(
        this._reportService.addBlockTimeRecord(this.blockTimeReportRecord, this.isEditBlockTimeRecord).subscribe({
          next: (): void => {
            this.isEditBlockTimeRecord = false;
            this.hideModel();
            this.getBlockTimeReportData();
            this.notificationService.success(`Record ${this.isEditBlockTimeRecord ? 'Edited' : 'Added'} Successfully!`);
          },
          error: (error: HttpErrorResponse): void => {
            this._reportService.loaderState.next(false);
            this.handleApiError(error?.error?.errors);
          }
        })
      );
    }
  }

  private hideModel() {
    this.tailNumberValue = null;
    this.airVendorValue = null;

    if (this.cancelBtnRef) {
      this.cancelBtnRef.nativeElement.click();
    }
  }

  editBlockTimeReportData(blockTimeReportForm: NgForm): void {
    this.blockTimeReportRecord.customer = blockTimeReportForm.controls['customer']?.value;
    this.blockTimeReportRecord.outboundReposition = this.isObjectAndHasValue(blockTimeReportForm, 'outboundReposition');
    this.blockTimeReportRecord.inboundReposition = this.isObjectAndHasValue(blockTimeReportForm, 'inboundReposition');
    this.blockTimeReportRecord.estimatedBlockHours = blockTimeReportForm.controls['estimatedBlockHours']?.value;
    this.blockTimeReportRecord.actualBlockTime = blockTimeReportForm.controls['actualBlockTime']?.value;
    this.blockTimeReportRecord.cost = blockTimeReportForm.controls['cost']?.value;
    this.blockTimeReportRecord.profit = blockTimeReportForm.controls['profit']?.value;
    this.blockTimeReportRecord.rate = blockTimeReportForm.controls['rate']?.value;
    this.blockTimeReportRecord.flightMargin = blockTimeReportForm.controls['flightMargin']?.value;
    this.blockTimeReportRecord.invoiceID = blockTimeReportForm.controls['invoiceID']?.value;
    this.blockTimeReportRecord.note = blockTimeReportForm.controls['note']?.value;
    this.blockTimeReportRecord.ar = blockTimeReportForm.controls['ar']?.value;
    this.blockTimeReportRecord.ap = blockTimeReportForm.controls['ap']?.value;
    this.blockTimeReportRecord.serviceType = this.isObjectAndHasValue(blockTimeReportForm, 'serviceType');
    this.blockTimeReportRecord.aircraftType = this.isObjectAndHasValue(blockTimeReportForm, 'aircraftType');
    this.blockTimeReportRecord.aircraftSize = this.isObjectAndHasValue(blockTimeReportForm, 'aircraftSize');
  }

  deleteBlockTimeRecord(blockTimeRecordId: string) {
    if (confirm('Do you want to delete the record?')) {
      this._reportService.loaderState.next(true);
      this._subscriptions.add(this._subscriptions.add(this._reportService.deleteBlockTimeRecord(blockTimeRecordId).subscribe((response) => {
        if (response) {
          this.successToast('Record deleted successfully!');
          this.getBlockTimeReportData();
        } else {
          this.warningToast('Something went wrong while deleting the blocktime record!');
        }
      }, (error) => {
        console.log(error);
        this.warningToast('Something went wrong while deleting the blocktime record!');
      })));
    }
  }

  openSplitBlockTimeRecordPopup(record: IBlockTimeItem) {
    const dataToFeed: ISplitRecordPopupDataViewModel = {
      blocktimeRecord: record,
      customerList: this.customerList
    };
    this._dialog.open(SplitRecordDialogComponent, {
      position: {
        top: '4rem'
      },
      width: '80%',
      height: 'fit-content',
      data: dataToFeed
    });
  }

  isArrayAndHasValue(value: any): boolean {
    return Array.isArray(value) && Array.from(value).length > 0;
  }

  isObjectAndHasValue(form: NgForm, type: string) {
    if (form.controls[type]?.value?.constructor === Array && form.controls[type]?.value.length === 0) {
      return null;
    } else {
      return form.controls[type]?.value;
    }
  }

  initializeAddBlockTimeRecordPopUp(value?: IBlockTimeItem): void {
    this.blockTimeReportRecord = {
      blockTimeReportID: null,
      aircraftSize: value ? value.aircraftSize : null,
      aircraftType: value ? value.aircraftType : null,
      airVendor: this.airVendorValue?.name ?? null,
      ap: value ? value.ap : null,
      ar: value ? value.ar : null,
      estimatedBlockHours: value ? value.estimatedBlockHours : null,
      actualBlockTime: value ? value.actualBlockTime : null,
      cancellationHours: value ? value.cancellationHours : null,
      totalBilledHours: value?.totalBilledHours,
      cost: value ? value.cost : null,
      customer: value ? value.customer : null,
      srCreatedOnDate: value ? value.srCreatedOnDate : null,
      invoiceID: value ? value.invoiceID : null,
      additionalOrgans: value ? value.additionalOrgans : null,
      profit: value ? value.profit : null,
      rate: value ? this.blockTimeReportRecord ? this.blockTimeReportRecord.rate : null : null,
      flightMargin: value ? this.blockTimeReportRecord ? this.blockTimeReportRecord.flightMargin : null : null,
      outboundReposition: value && !Array.isArray(value.outboundReposition) ? value.outboundReposition : null,
      inboundReposition: value && !Array.isArray(value.inboundReposition) ? value.inboundReposition : null,
      routing: value ? value.routing : null,
      groundVendor: value && this.isArrayAndHasValue(value.groundVendor) ? value.groundVendor : null,
      serviceType: value ? value.serviceType : null,
      tailNumber: value ? value.tailNumber : null,
      tripID: value ? value.tripID : null,
      unosid: value ? value.unosid : null,
      ActualEndTime: value ? value.ActualEndTime : null,
      ActualStartTime: value ? value.ActualStartTime : null,
      plannedEndTime: value ? value.plannedEndTime : null,
      plannedStartTime: value ? value.plannedStartTime : null,
      primaryOrgan: value && this.isArrayAndHasValue(value.primaryOrgan) ? value.primaryOrgan : null,
      region: value ? value.region : null,
      note: value ? value.note : null,
      LastUpdatedBy: null,
      lastUpdatedOn: null,
      isRecordManuallyAdded: true,
      isRecordSplitted: false,
      monthYear: value?.monthYear ?? '',
      ibEstimatedBlockHours: value ? value.ibEstimatedBlockHours : null,
      obEstimatedBlockHours: value ? value.obEstimatedBlockHours : null,
      liveFlightHours: value ? value.liveFlightHours : null,
      isCancelledNoCost: value ? value.isCancelledNoCost : false
    };
  }

  onPageChange() {
    this.updatePagination();
  }

  updatePagination() {
    this.pagination = Constant.showPaginationMessage(this.blockTimeReportData.length, this.currentPageNumber, this.selectedPage);
    this.convertSubTotal();
  }

  closeBlockTimeReportModel(): void {
    this.isOpenAddEditBlockTimeRecordPopUp = !this.isOpenAddEditBlockTimeRecordPopUp;
    this.isEditBlockTimeRecord = false;
    this.monthYearList = reportMonthYearList();
    this.tailNumberValue = null;
    this.airVendorValue = null;
    this.aircrafts = [];
  }

  calculateRateAndFlightMargin() {
    const profit = this.blockTimeReportRecord.profit;
    const cost = this.blockTimeReportRecord.cost;
    const rate = (profit / cost) * 100;
    const flightMargin = (profit / (cost + profit)) * 100;
    this.blockTimeReportRecord.rate = !Number.isNaN(rate) ? Number(rate.toFixed(2)) : 0;
    this.blockTimeReportRecord.flightMargin = !Number.isNaN(flightMargin) ? Number(flightMargin.toFixed(2)) : 0;
  }

  getAllAirportCodeList() {
    this._subscriptions.add(
      this._locationService.getAllAirportCodeList().subscribe(response => {
        this.airportCodes = response.map(item => item.airportCode);
      })
    );
  }

  calculateTotalValues(): void {
    this.convertSum();
    this.convertSubTotal();
  }

  private convertSubTotal(): void {
    const blockTimeRecordListOfCurrentPage = this.blockTimeReportData.slice((this.currentPageNumber - 1) * this.selectedPage, this.selectedPage * this.currentPageNumber);

    this.totalOfBlockTimeReportValues = this.totalOfBlockTimeReportValues.map(item => {
      if (item.prop === 'count') {
        return {
          ...item,
          subTotal: blockTimeRecordListOfCurrentPage.length,
        };
      }

      if (item.prop === 'sumRate') {
        const costKey = subTotalReportSum['sumCost'];
        const profitKey = subTotalReportSum['sumProfit'];
        const costValue = blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[costKey] ?? 0), 0);
        const profitValue = blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[profitKey] ?? 0), 0);

        const subTotal = this.calculateTotalRate(profitValue, costValue);

        return {
          ...item,
          subTotal,
        };
      }

      if (item.prop === 'sumFlightMargin') {
        const costKey = subTotalReportSum['sumCost'];
        const profitKey = subTotalReportSum['sumProfit'];
        const costValue = blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[costKey] ?? 0), 0);
        const profitValue = blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[profitKey] ?? 0), 0);

        const subTotal = this.calculateTotalFlightMargin(profitValue, costValue);

        return {
          ...item,
          subTotal,
        };
      }

      const responseKey = subTotalReportSum[item.prop];
      const subTotal = responseKey
        ? blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[responseKey] ?? 0), 0)
        : 0;
      return {
        ...item,
        subTotal,
      };
    });
  }

  private convertSum(): void {
    this.totalOfBlockTimeReportValues = this.totalOfBlockTimeReportValues.map(item => {
      return {
        ...item,
        sum: this.totalSum[item.prop] ?? 0
      };
    });
  }

  updateArOrAp(record: IBlockTimeItem, fieldName: string) {
    this._subscriptions.add(this._reportService.updateArOrAP(record).subscribe((response) => {
      if (response) {
        this.successToast(`Value of ${fieldName} is updated successfully.`);
      } else {
        this.warningToast(`Unable to update the value of ${fieldName} !`);
      }
    }));
  }

  warningToast(message: string) {
    this.notificationService.warning(message);
  }

  successToast(message: string) {
    this.notificationService.success(message);
  }

//TODO: Need to update the logic to show poper using angular approach
  togglePopOver() {
    $(document).ready(function () {
      $('[data-toggle="popover"]').popover();
    });
  }

  onTripChange(tripId: string): void {
    const getTripBlockInfoSubs = this._reportService.getTripBlockInfo(tripId).subscribe(data => {
      this.blockTimeReportRecord.srCreatedOnDate = data.srCreatedOnDate;
      this.blockTimeReportRecord.serviceType = data.serviceType;
      this.blockTimeReportRecord.additionalOrgans = data.serviceSubType;
      this.blockTimeReportRecord.unosid = data.unosno;
      this.blockTimeReportRecord.groundVendor = data.groundVendor;
      this.blockTimeReportRecord.customer = data.customer;
      this.monthYearList = reportMonthYearList(data.srCreatedOnDate);
    });

    this._subscriptions.add(getTripBlockInfoSubs);
  }

  private getTripsBasedOnTripNumber(): void {
    const getTripsBasedOnTripNumberSubs = this._serviceRequestService.getTripsBasedOnTripNumber().subscribe(data => {
      this.trips = data;
    });

    this._subscriptions.add(getTripsBasedOnTripNumberSubs);
  }

  onSelectAirVendor(event: BaseDropdownInterface): void {
    this.blockTimeReportRecord.airVendor = event.name;
    this.blockTimeReportRecord.tailNumber = null;
    this.blockTimeReportRecord.aircraftType = null;
    this.blockTimeReportRecord.aircraftSize = null;
    this.tailNumberValue = null;

    const getAircraftsForVendorsSubs = this._reportService.getAircraftsForVendors(event.id).subscribe(data => {
      this.aircrafts = data;

      if (this.airVendorValue) {
        this.aircrafts.find(item => item.tailNumber === this.blockTimeReportRecord.tailNumber);
      }
    });

    this._subscriptions.add(getAircraftsForVendorsSubs);
  }

  onTailNumberChange(aircraft: AircraftInterface): void {
    this.blockTimeReportRecord.aircraftType = aircraft.aircraftType;
    this.blockTimeReportRecord.aircraftSize = aircraft.aircraftSize;
  }

  calculateActualBlockTime(): void {
    const obEstimatedBlockHours = Number(this.blockTimeReportRecord.obEstimatedBlockHours) ?? 0;
    const ibEstimatedBlockHours = Number(this.blockTimeReportRecord.ibEstimatedBlockHours) ?? 0;
    const liveFlightHours = Number(this.blockTimeReportRecord.liveFlightHours) ?? 0;
    const cancellationHours = this.blockTimeReportRecord.isCancelledNoCost ? (Number(this.blockTimeReportRecord.cancellationHours) ?? 0) : 0;
    const sum = obEstimatedBlockHours + ibEstimatedBlockHours + liveFlightHours + cancellationHours;
    this.checkPositiveHours(obEstimatedBlockHours, 'OB Repo Hours');
    this.checkPositiveHours(ibEstimatedBlockHours, 'IB Repo Hours');
    this.checkPositiveHours(liveFlightHours, 'Live Flight Hours');
    // this.checkPositiveHours(cancellationHours, 'Cancellation Hours');
    this.checkPositiveHours(sum, 'Actual Block Time');
    this.blockTimeReportRecord.actualBlockTime = Number(sum * 100 / 100).toFixed(2);
  }

  private convertFormToRecord(form: NgForm): IBlockTimeItem {
    return {
      ...form.value,
      airVendor: this.airVendorValue[0]?.name ?? '',
      tailNumber: this.tailNumberValue ? this.tailNumberValue?.tailNumber : '',
    };
  }

  private getCustomers(record?: IBlockTimeItem): void {
    const customerListSubs = this.memberService.getCustomerList()
      .pipe(first())
      .subscribe({
        next: (response: CustomerList[]): void => {
          this.customerList = response;

          if (record) {
            this.blockTimeReportRecord.customer = this.customerList.find(x => x.customerName === record.customer.customerName);
          }
        }
      });

    this._subscriptions.add(customerListSubs);
  }

  private initHoursValue(value: number, isCancelledNoCost: boolean, initialValue = 0): string {
    if (isCancelledNoCost || value === 0) {
      return '0';
    }

    if (!value) {
      return Number(initialValue).toFixed(2);
    }

    return Number(value).toFixed(2);
  }

  private checkPositiveHours(fieldLabel: number, fieldName: string): void {
    if (fieldLabel < 0) {
      alert(`Can not save a Negative Value. Please update ${fieldName}.`);
    }
  }

  private handleApiError(errors: string[]): void {
    if (errors?.length) {
      errors.forEach(err => this.notificationService.error(err));
    }
  }

  private calculateTotalFlightMargin(profit: number, cost: number): number {
    if (profit > 0 && cost > 0) {
      const flightMargin = (profit / (cost + profit)) * 100;
      return Number(flightMargin.toFixed(2));
    }
    return 0;
  }

  private calculateTotalRate(profit: number, cost: number): number {
    if (profit > 0 && cost > 0) {
      const rate = (profit / cost) * 100;
      return Number(rate.toFixed(2));
    }
    return 0;
  }
}
